// src/pages/Quotes.tsx — HSC-01 (TECH_DEBT #31): quotes, pruning plans and refunds.
// Reads ops_quotes_overview / ops_quote_detail / ops_plans_overview / ops_refunds_overview (viewer).
// Actions (pricing_admin, logged with a reason): retry or mark done a failed refund, cancel a plan.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ops, type QuoteRow, type QuotesOverview, type PlansOverview, type RefundsOverview, type QuoteDetail } from "../lib/ops";
import { money, dateTime } from "../lib/format";
import { supabase } from "../lib/supabase";
import { Chip, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";

type Tab = "quotes" | "plans" | "refunds";
const SOURCE: Record<string, string> = { customer_page: "Customer page", canvas: "Canvas", ops: "Ops", maestro_addon: "On-site add-on", plan: "Plan visit" };
const cell: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--line, #e5e0d6)", verticalAlign: "top", textAlign: "left" };
const head: React.CSSProperties = { ...cell, fontWeight: 600, fontSize: 13, color: "var(--sec, #666)" };

function statusChip(s: string) {
  const tone = s === "paid" || s === "done" ? "info" : s === "failed" || s === "expired" || s === "skipped" ? "warn" : "muted";
  return <Chip tone={tone as "info" | "warn" | "muted"}>{s.replace(/_/g, " ")}</Chip>;
}

function QuoteDetailRow({ number }: { number: string }) {
  const [d, setD] = useState<QuoteDetail | null>(null);
  const [urls, setUrls] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    ops.quoteDetail(number).then(async (x) => {
      if (!live) return;
      setD(x);
      const paths: string[] = Array.isArray(x.photos) ? x.photos : [];
      const signed = await Promise.all(paths.map(async (p) => (await supabase.storage.from("quote-photos").createSignedUrl(p, 600)).data?.signedUrl ?? ""));
      if (live) setUrls(signed.filter(Boolean));
    }).catch((e) => live && setErr((e as Error).message));
    return () => { live = false; };
  }, [number]);
  if (err) return <Notice kind="error">{err}</Notice>;
  if (!d) return <Loading what="quote" />;
  return (
    <div style={{ padding: "8px 4px 14px" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
        {(d.lines ?? []).map((l) => (
          <tr key={l.line_no}><td style={cell}>{l.description}</td><td style={cell}>{l.quantity} {l.unit}</td>
            <td style={cell}>{money(l.unit_price_cents)}</td><td style={{ ...cell, fontWeight: 600 }}>{money(l.line_total_cents)}</td></tr>))}
      </tbody></table>
      <p className="hint">Pay link: <a href={d.pay_url} target="_blank" rel="noreferrer">{d.pay_url}</a></p>
      {(d.addons ?? []).length > 0 && <p className="hint">Add-ons: {(d.addons ?? []).map((a) => `${a.quote_number} (${a.status}, ${money(a.total_cents)})`).join(" · ")}</p>}
      {(d.refunds ?? []).length > 0 && <p className="hint">Refunds: {(d.refunds ?? []).map((r) => `${money(r.amount_cents)} ${r.status}${r.error ? ` — ${r.error}` : ""}`).join(" · ")}</p>}
      {urls.length > 0 && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {urls.map((u) => <a key={u} href={u} target="_blank" rel="noreferrer"><img src={u} alt="Customer photo" style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 8 }} /></a>)}</div>}
    </div>
  );
}

function QuotesTab({ data }: { data: QuotesOverview }) {
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const rows = useMemo(() => data.quotes.filter((q) => filter === "all" || q.status === filter || q.source === filter), [data, filter]);
  const who = (q: QuoteRow) => [q.contact?.name, q.contact?.email, q.contact?.phone].filter(Boolean).join(" · ") || "—";
  return (
    <>
      <p className="hint">Open: {money(data.open_total_cents)} · Paid: {money(data.paid_total_cents)} · {Object.entries(data.counts ?? {}).map(([k, n]) => `${n} ${k}`).join(" · ")}</p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0" }}>
        {["all", "quoted", "paid", "expired", "customer_page", "canvas", "maestro_addon", "plan"].map((f) => (
          <button key={f} type="button" className="btn" aria-pressed={filter === f} onClick={() => setFilter(f)}
            style={{ opacity: filter === f ? 1 : 0.65 }}>{SOURCE[f] ?? f}</button>))}
      </div>
      <div style={{ overflowX: "auto" }}><table style={{ borderCollapse: "collapse", width: "100%", minWidth: 760 }}>
        <thead><tr><th style={head}>Quote</th><th style={head}>From</th><th style={head}>Customer</th><th style={head}>Total</th><th style={head}>Status</th><th style={head}>Photos</th><th style={head}>Created</th></tr></thead>
        <tbody>{rows.map((q) => (
          <FragmentRow key={q.quote_number} q={q} open={open === q.quote_number} onToggle={() => setOpen(open === q.quote_number ? null : q.quote_number)} who={who(q)} />))}
        </tbody></table></div>
      {rows.length === 0 && <p className="hint">No quotes here yet.</p>}
    </>
  );
}
function FragmentRow({ q, open, onToggle, who }: { q: QuoteRow; open: boolean; onToggle: () => void; who: string }) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer" }}>
        <td style={cell}><b>{q.quote_number}</b>{q.parent_quote_number ? <div className="hint">add-on to {q.parent_quote_number}</div> : null}</td>
        <td style={cell}>{SOURCE[q.source] ?? q.source}<div className="hint">{q.product_key}</div></td>
        <td style={cell}>{who}<div className="hint">{q.zip ?? ""}</div></td>
        <td style={cell}>{money(q.total_cents)}{q.refunded_cents ? <div className="hint">refunded {money(q.refunded_cents)}</div> : null}</td>
        <td style={cell}>{statusChip(q.status)}{q.scheduled_date ? <div className="hint">for {q.scheduled_date}</div> : null}</td>
        <td style={cell}>{q.photos_needed ? `${q.photos_received}/${q.photos_needed}` : "—"}</td>
        <td style={cell}>{dateTime(q.created_at)}</td>
      </tr>
      {open && <tr><td colSpan={7} style={{ ...cell, background: "var(--panel, #faf8f3)" }}><QuoteDetailRow number={q.quote_number} /></td></tr>}
    </>
  );
}

function PlansTab({ data, onChanged }: { data: PlansOverview; onChanged: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  const cancel = async (id: string) => {
    const note = window.prompt("Why is this plan being cancelled? (at least 5 characters)");
    if (!note) return;
    try { const r = await ops.cancelPlan(id, note); setMsg(r.ok ? `Plan cancelled (${r.visits_cancelled} visits).` : `Not cancelled: ${r.reason}`); onChanged(); }
    catch (e) { setMsg((e as Error).message); }
  };
  return (
    <>
      {msg && <Notice kind="info">{msg}</Notice>}
      <p className="hint">{data.plans.filter((p) => p.status === "active").length} active plans · {data.maps_without_plan} Pruning Maps without a plan yet</p>
      {data.plans.map((p) => (
        <div key={p.plan_id} className="card" style={{ padding: 12, margin: "10px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div><b>{p.customer?.name ?? "Customer"}</b> · {p.level} · {money(p.per_visit_cents)} a visit {statusChip(p.status)}
              <div className="hint">{[p.customer?.email, p.customer?.phone, p.zip].filter(Boolean).join(" · ")} · credit left {money(p.credit_remaining_cents)} · price good until {p.price_valid_until}</div></div>
            {p.status === "active" && <button type="button" className="btn" onClick={() => cancel(p.plan_id)}>Cancel plan</button>}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {(p.visits ?? []).map((v) => <span key={v.visit_no} className="hint" style={{ border: "1px solid var(--line, #e5e0d6)", borderRadius: 8, padding: "4px 8px" }}>
              {v.month.slice(0, 7)} · {v.status.replace(/_/g, " ")}{v.quote_number ? ` · ${v.quote_number}` : ""}</span>)}
          </div>
        </div>))}
      {data.open_maps.length > 0 && <>
        <SectionLabel>Pruning Maps waiting for a plan</SectionLabel>
        {data.open_maps.map((m) => <p key={m.map_id} className="hint">{m.customer ?? "Customer"} · {m.zip} · mapped {dateTime(m.created_at)} · credit until {m.credit_until} · <a href={m.signup_url} target="_blank" rel="noreferrer">sign-up link</a></p>)}
      </>}
      {data.plans.length === 0 && data.open_maps.length === 0 && <p className="hint">No Pruning Maps or plans yet.</p>}
    </>
  );
}

function RefundsTab({ data, onChanged }: { data: RefundsOverview; onChanged: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  const act = async (id: string, action: "retry" | "mark_done") => {
    const note = window.prompt(action === "retry" ? "Why retry? (at least 5 characters)" : "How was it refunded? (e.g. refunded in the Stripe dashboard)");
    if (!note) return;
    try { const r = await ops.refundAction(id, action, note); setMsg(r.ok ? (action === "retry" ? "Queued — the refund is retried within 10 minutes." : "Marked as refunded.") : `Not changed: ${r.reason}`); onChanged(); }
    catch (e) { setMsg((e as Error).message); }
  };
  return (
    <>
      {msg && <Notice kind="info">{msg}</Notice>}
      <p className="hint">{Object.entries(data.counts ?? {}).map(([k, n]) => `${n} ${k}`).join(" · ") || "No refunds yet."}</p>
      <div style={{ overflowX: "auto" }}><table style={{ borderCollapse: "collapse", width: "100%", minWidth: 700 }}>
        <thead><tr><th style={head}>Quote</th><th style={head}>Customer</th><th style={head}>Amount</th><th style={head}>Status</th><th style={head}>Detail</th><th style={head}></th></tr></thead>
        <tbody>{data.refunds.map((r) => (
          <tr key={r.id}>
            <td style={cell}>{r.quote_number}</td><td style={cell}>{r.customer ?? "—"}</td><td style={cell}>{money(r.amount_cents)}</td>
            <td style={cell}>{statusChip(r.status)}</td>
            <td style={cell}>{r.error ?? r.stripe_refund_id ?? r.reason}<div className="hint">{dateTime(r.created_at)}</div></td>
            <td style={cell}>{r.status === "failed" && <div style={{ display: "flex", gap: 6 }}>
              <button type="button" className="btn" onClick={() => act(r.id, "retry")}>Retry</button>
              <button type="button" className="btn" onClick={() => act(r.id, "mark_done")}>Mark refunded</button></div>}</td>
          </tr>))}</tbody></table></div>
    </>
  );
}

export default function Quotes() {
  const [tab, setTab] = useState<Tab>("quotes");
  const [q, setQ] = useState<QuotesOverview | null>(null);
  const [p, setP] = useState<PlansOverview | null>(null);
  const [r, setR] = useState<RefundsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { const [a, b, c] = await Promise.all([ops.quotesOverview(), ops.plansOverview(), ops.refundsOverview()]); setQ(a); setP(b); setR(c); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const failed = r?.refunds.filter((x) => x.status === "failed").length ?? 0;
  const awaitingPhotos = q?.quotes.filter((x) => x.status === "quoted" && x.photos_needed > x.photos_received).length ?? 0;
  return (
    <PageFrame title="Quotes" lede="Customer-built quotes, Canvas proposals, on-site add-ons and plan visits — plus pruning plans and refunds.">
      {error && <Notice kind="error">{error}</Notice>}
      {!q || !p || !r ? <Loading what="quotes" /> : <>
        {(failed > 0 || awaitingPhotos > 0) && <Notice kind="info">
          {failed > 0 && <b>{failed} failed refund{failed === 1 ? "" : "s"} need a person. </b>}
          {awaitingPhotos > 0 && <span>{awaitingPhotos} open quote{awaitingPhotos === 1 ? "" : "s"} still waiting for customer photos.</span>}
        </Notice>}
        <div style={{ display: "flex", gap: 6, margin: "10px 0 14px" }}>
          {(["quotes", "plans", "refunds"] as Tab[]).map((t) => (
            <button key={t} type="button" className="btn" aria-pressed={tab === t} onClick={() => setTab(t)} style={{ opacity: tab === t ? 1 : 0.65 }}>
              {t === "quotes" ? `Quotes (${q.quotes.length})` : t === "plans" ? `Plans (${p.plans.length})` : `Refunds (${r.refunds.length}${failed ? `, ${failed} failed` : ""})`}</button>))}
          <button type="button" className="btn" onClick={() => void load()} style={{ marginLeft: "auto" }}>Refresh</button>
        </div>
        {tab === "quotes" && <QuotesTab data={q} />}
        {tab === "plans" && <PlansTab data={p} onChanged={load} />}
        {tab === "refunds" && <RefundsTab data={r} onChanged={load} />}
      </>}
    </PageFrame>
  );
}
